import React, { useEffect, useRef } from 'react';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { Box, InputBase, makeStyles, Typography, withStyles } from '@material-ui/core';
import { DropDownIcon } from '../../../../../../components/Icon';
import { classNames, conditional } from '../../../../../../utils';

const useStyles = makeStyles((theme) => ({
  inputBox: (props) => ({
    position: 'relative',
    height: props.height ? props.height : '3.5rem',
    width: props.width ? props.width : '100%',
    border: props.border ?? 'solid 1px #e4e7eb',
    borderRadius: '0.625rem',
    background: '#fff',
  }),
  inputBase: (props) => ({
    height: '100%',
    width: '100%',
    lineHeight: props.height ? `${props.height}rem` : '3.5rem',
    borderRadius: '0.625remm',
    paddingLeft: '1.25rem',
    '& .MuiInputBase-input': {
      fontSize: '1rem',
      color: '#2e4469',
      fontFamily: 'NotoSans-Medium',
      height: 'unset',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
  }),
  dropDownIcon: {
    position: 'absolute',
    top: '50%',
    right: '1rem',
    transform: 'translateY(-50%)',
    height: '0.875rem',
    width: '0.875rem',
    color: '#538fff',
    '&:hover': {
      cursor: 'pointer',
    },
  },
  iconDisabled: {
    color: '#C0C6CB',
  },
  disabled: {
    background: '#f5f5f5 !important',
    '& ::-webkit-input-placeholder': {
      /* WebKit browsers */ color: '#c0c6cb',
    },

    '& ::-moz-placeholder': {
      /* Mozilla Firefox 19+ */ color: '#c0c6cb',
    },

    '& :-ms-input-placeholder': {
      /* Internet Explorer 10+ */ color: '#c0c6cb',
    },
  },
  error: {
    borderColor: '#f44336 !important',
  },
  errorMsg: ({ errMsgMargin, errMsgWidth }) => ({
    margin: errMsgMargin,
    color: '#f44336',
    fontFamily: 'NotoSans-Medium',
    fontWeight: '500',
    width: errMsgWidth,
  }),
}));

function AutoCompleteTimeInput({
  id,
  classes,
  itemList,
  className,
  placeholder,
  value,
  onChange,
  width,
  height,
  style,
  onOpen,
  onBlur,
  errorMsg,
  error,
  dropDownIcon,
  onFocus,
  errMsgMargin = '3px 0.825rem 0 0.825rem',
  errMsgWidth = 'auto',
  disabled,
  freeSolo = false,
  clearOnblur = !freeSolo,
  border,
  renderOption,
  renderInput,
  inputProps,
  open,
  onClose,
  onInput,
  filterOptions,
  disabledSelectionAutoCompletion,
  ListboxComponent,
  maxLength,
}) {
  const styles = useStyles({ width, height, errMsgMargin, border, errMsgWidth });

  const [inputValue, setInputValue] = React.useState(freeSolo ? value : '');
  const valueCtr = () => {
    const result =
      Array.isArray(itemList) &&
      itemList.find((item) => {
        return item.value === value;
      });
    return conditional(result, result, null);
  };

  const getErroeClass = (classes) => {
    return error || errorMsg ? classNames(classes, styles.error) : classes;
  };

  const inputBody = (params) => {
    const dropDownHandle = () => {
      params.inputProps.ref.current.childNodes[0].focus();
      params.inputProps.onMouseDown();
    };
    const getDisabledClass = (props) => {
      return disabled ? classNames(props, styles.disabled) : props;
    };
    return (
      <div ref={disabled ? null : params.InputProps.ref} className={getDisabledClass(getErroeClass(styles.inputBox))}>
        <InputBase
          {...params.inputProps}
          {...inputProps}
          placeholder={placeholder}
          className={styles.inputBase}
          type="text"
          onBlur={(e) => {
            params.inputProps.onBlur(e);
            if (typeof onBlur === 'function') {
              onBlur();
            }
          }}
          onFocus={onFocus}
          inputProps={{ maxLength }}
        />
        {dropDownIcon && (
          <DropDownIcon
            className={disabled ? classNames(styles.dropDownIcon, styles.iconDisabled) : styles.dropDownIcon}
            onClick={disabled ? null : dropDownHandle}
          />
        )}
      </div>
    );
  };

  const renderOptionCtr = (option) => {
    const IeftIcon = option.leftIcon;
    return (
      <>
        {IeftIcon ? <IeftIcon /> : ''}
        {option.text}
      </>
    );
  };

  const handleHasEvent = ({ reason, event, newInputValue }) => {
    if (reason === 'reset' && onChange && Array.isArray(itemList)) {
      const temp = itemList.find((item) => {
        return item.text === inputValue;
      });
      if (temp) {
        onChange(inputValue);
        !disabledSelectionAutoCompletion && setInputValue(temp.text);
      } else {
        onChange('');
        !disabledSelectionAutoCompletion && setInputValue('');
      }
      return;
    }
    if (freeSolo) {
      setInputValue(event.target.value);
      if (typeof onInput === 'function') {
        onInput(event.target.value);
      }
    } else {
      setInputValue(newInputValue);
    }
  };

  const handleInputChange = (event, newInputValue, reason) => {
    if (event) {
      handleHasEvent({ reason, event, newInputValue });
    }
    if (freeSolo) {
      if (reason === 'reset') {
        onChange(value);
        return;
      }
      if (newInputValue || newInputValue === '') {
        setInputValue(newInputValue);
        if (typeof onChange === 'function') {
          onChange(newInputValue);
        }
      }
    } else {
      setInputValue(newInputValue);
    }
  };

  const isInitialMount = useRef(true);

  useEffect(() => {
    if (freeSolo) {
      if (isInitialMount.current && value !== '') {
        setInputValue('');
        isInitialMount.current = false;
      } else {
        setInputValue(value);
      }
    }
  }, [value, freeSolo]);

  return (
    <Box display="flex" style={style} className={className} flexDirection="column">
      <Autocomplete
        id={id}
        classes={classes}
        className={classNames(styles.root, className)}
        value={valueCtr()}
        onChange={(event, newValue) => {
          !disabledSelectionAutoCompletion && setInputValue(newValue?.text ?? '');
          if (typeof onChange === 'function') {
            onChange(newValue?.value ?? '');
          }
        }}
        open={open}
        inputValue={inputValue}
        disabled={disabled}
        onInputChange={handleInputChange}
        options={Array.isArray(itemList) ? itemList : []}
        getOptionLabel={(option) => option.text}
        renderInput={renderInput ?? ((params) => inputBody(params))}
        style={style}
        renderOption={
          renderOption ??
          ((option) => {
            return <>{renderOptionCtr(option)}</>;
          })
        }
        onOpen={onOpen}
        freeSolo={freeSolo}
        onClose={onClose}
        onInput={onInput}
        filterOptions={filterOptions}
        ListboxComponent={ListboxComponent}
        clearOnBlur={clearOnblur}
      />
      {errorMsg ? <Typography className={styles.errorMsg}>{errorMsg}</Typography> : ''}
    </Box>
  );
}

export default withStyles({
  paper: {
    boxShadow: '0 2px 10px 0 rgba(0, 0, 0, 0.1)',
  },
  listbox: {
    '& li[aria-selected="true"]': {
      color: 'unset',
      background: 'unset',
      cursor: 'pointer',
    },
    '& li[data-focus="true"]': {
      color: '#538fff',
      background: '#f5faff',
      cursor: 'pointer',
    },
  },
  option: {
    fontFamily: 'NotoSans-Medium',
    fontSize: '1rem',
    fontWeight: '500',
    color: '#2e4469',
  },
})(AutoCompleteTimeInput);
